<template>
    <div v-if="psdLayers">
        <div class="row align-center">
            <div class="col-12">
                <div v-if="psdLayers.type == 'PSD'">
                    <h6 class="font-weight-bold no-wrap align-center" v-text="'PSD'" />
                    <modal-import-list-item :item="psdHeader" :prevent-action="true" />
                </div>
                <template v-else>
                    <div class="d-flex flex-column">
                        <h6 class="font-weight-bold no-wrap align-center" v-text="'Design'" />
                        <div class="list-group-item d-inline-flex border-0 px-0 disabled">
                            <div v-if="psdHeader.thumb" class="mr-2" style="height: 97px; width: 136px">
                                <img
                                    :src="getThumbUrl(psdHeader.thumb)"
                                    class="img-thumbnail p-0 img-fluid h-100 w-100"
                                    alt=""
                                />
                            </div>
                            <span>{{ psdHeader.name }}</span>
                        </div>
                        <div class="mt-3 mb-3">
                            <div class="mb-1">
                                <span>Import</span>
                            </div>
                            <c-button-group
                                :value="psdHeader.mode"
                                type="radio"
                                :items="importModes"
                                no-icon
                                @input="onImportModeChange"
                            />
                        </div>
                        <c-select
                            class="flex-fill w-50"
                            :value="selectedOption"
                            :options="pages"
                            label="Select one page to import"
                            label-class="text-bold mb-2"
                            @input="onPageChange"
                        >
                            <template #selected-option="{ thumb, label }">
                                <page-file-option :thumb="thumb" :label="label" />
                            </template>
                            <template #option="{ thumb, label }">
                                <page-file-option :thumb="thumb" :label="label" />
                            </template>
                        </c-select>
                        <span class="mt-3">Import design dimensions</span>
                        <b-form-checkbox v-model="importPageDimensions" name="check-button" switch />
                        <div v-if="psdHeader.mode == 'PNG'">
                            <h6 class="font-weight-bold no-wrap align-center mt-3">
                                Do you want to import with a transparent background?
                            </h6>
                            <div
                                class="d-flex justify-content-between align-items-center png-transparency-disclaimer mb-3"
                            >
                                <font-awesome-icon class="mr-2" :icon="['fas', 'circle-info']" />
                                <div class="notification-message mb-1 mr-2">
                                    To import with a transparent background you must have Canva Pro. If you don't have
                                    it, it will still be imported with a background.
                                </div>
                            </div>
                            <loader-btn
                                variant="primary"
                                loading-text="Importing..."
                                :loading="importingPngTransparent"
                                :disabled="showLoader"
                                @click="importAsPngLayer(true)"
                            >
                                Make it transparent
                            </loader-btn>
                            <loader-btn
                                variant="outline-primary"
                                class="ml-2"
                                loading-text="Importing..."
                                :loading="importingPngNonTransparent"
                                :disabled="showLoader"
                                @click="importAsPngLayer()"
                            >
                                Import with background
                            </loader-btn>
                        </div>
                    </div>
                </template>
            </div>
        </div>

        <template v-if="psdHeader.mode == 'LAYERS'">
            <div class="row mt-4 align-center">
                <div class="col-12 col-sm-7">
                    <h6 class="font-weight-bold no-wrap align-center">Select the layers to import</h6>
                </div>
                <div class="col-12 col-sm-5">
                    <modal-import-search @search-text-change="updateSearchText" />
                </div>
            </div>
            <div class="mt-2 mb-2">
                <div class="col-12 layer-tree">
                    <div
                        v-if="showLoader"
                        class="align-items-center d-flex justify-content-center my-2 text-center my-2"
                    >
                        <b-spinner variant="primary" />
                        <strong class="ml-3 primary-text">{{ loaderMessage }}</strong>
                    </div>
                    <template v-else>
                        <modal-import-layers-tree :tree-item="filteredTreeItems" />
                        <b-pagination
                            v-model="currentPage"
                            class="mt-4"
                            :total-rows="pageElementsCount"
                            :per-page="perPage"
                            aria-controls="my-table"
                            align="center"
                        />
                    </template>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch, reactive } from 'vue';
import { Item, TreeItem, TreeType } from '@/components/Layers/modal/types';
import ModalImportSearch from '@/components/Layers/modal/import/search/ModalImportSearch.vue';
import ModalImportLayersTree from '@/components/Layers/modal/import/layers-tree/ModalImportLayersTree.vue';
import ModalImportListItem from '../../list/ModalImportListItem.vue';
import PageFileOption from './PageFileOption.vue';
import LoaderBtn from '@/components/LoaderBtn/LoaderBtn.vue';
import CSelect from '@/components/shared/CSelect/CSelect.vue';
import { CSelectItem, Primitive } from '@/components/shared/CSelect';
import CButtonGroup, { type CButtonGroupItem } from '@/components/shared/CButtonGroup/index';
import { fetchImageAsBlob, getImageDimensions, getThumbUrl } from '@/utils/imageUtils';
import type { ImportLayersElements, LayerElementsMetadata, ModalImportMode } from '@/typings/layers/layers';
import { useFileLayers } from '@/composables/externalFileParser';
import store from '@/store';
import { exportCanvaDesign } from '@/api/canva';
import { type CanvaDesignExportRequest, CanvaExportFormat } from '@/typings/parsers/externalFileParser';

const importModes: CButtonGroupItem<ModalImportMode>[] = [
    { label: 'Layers', value: 'LAYERS' },
    { label: 'PNG', value: 'PNG' }
];

const emits = defineEmits(['fire-import-layers'])

const importingPngTransparent = ref(false);
const importingPngNonTransparent = ref(false);
const showLoader = ref(false);
const loaderMessage = ref('Loading...');
const searchText = ref('');
const importPageDimensions = ref(true);
const psdLayers = ref<ImportLayersElements | undefined>(store.state.storeLayersModal?.parsedFileLayers);
const perPage = 8;
const currentPage = ref(1);

const user = computed(() => store.state.user);
const { data: layers, parseAndExtractLayers } = useFileLayers(psdLayers.value?.type!);

const psdHeader = reactive<Item>({
    id: '',
    thumb: psdLayers.value?.artWorkThumbnail || '',
    name: psdLayers.value?.artworkName || '',
    mode: psdLayers.value?.mode
})

const pages = computed<CSelectItem<number>[]>(
    () =>
        psdLayers.value?.pages.map((page) => ({
            label: `Page ${page.pageNumber}`,
            value: page.pageNumber,
            thumb: page.fileUrl
        })) || []
);
const selectedOption = ref<CSelectItem<number>>(pages.value[0]);
const selectedPage = computed(() => {
    if (!psdLayers.value) return;
    psdLayers.value.selectedPage = selectedOption.value.value;
    return selectedOption.value.value;
});
const page = computed(() => psdLayers.value?.pages.find((page) => page.pageNumber == selectedPage.value) || null);
const treeItemChildren = computed(() =>
    page.value
        ? page.value.elements.map(
              (element, index): TreeItem => ({
                  id: index + 1,
                  name: element.name,
                  icon: '',
                  type: element.type as TreeType,
                  metadata: element
              })
          )
        : []
);
const treeItemElements = computed(() => {
    const startIndex = (currentPage.value - 1) * perPage;
    const endIndex = startIndex + perPage;

    const childItems: TreeItem = {
        name: 'Select All',
        icon: '',
        type: TreeType.FROM_FILE_ELEMENT,
        paginatedChildren: treeItemChildren.value.slice(startIndex, endIndex) || [],
        children: treeItemChildren.value || []
    };
    return childItems;
});

const filteredTreeItems = computed(() => {
    const startIndex = (currentPage.value - 1) * perPage;
    const endIndex = startIndex + perPage;
    const { children: childrenTreeItems } = treeItemElements.value;
    if (!childrenTreeItems || !searchText.value) {
        return treeItemElements.value;
    }

    const filteredTreeItems = [...childrenTreeItems].filter((childTreeType) => {
        if (!childTreeType.name) return false;
        return childTreeType.name.toLowerCase().includes(searchText.value.toLowerCase());
    });

    const result = {
        ...treeItemElements.value,
        children: filteredTreeItems,
        paginatedChildren: filteredTreeItems.slice(startIndex, endIndex) || []
    };
    return result;
});

const pageElementsCount = computed(() => filteredTreeItems.value.children?.length || 0);

watch(importPageDimensions, () => {
    if (!psdLayers.value || psdLayers.value.type == 'PSD') return;
    psdLayers.value.includeDimensions = importPageDimensions.value;
});

onMounted(() => {
    if (!psdLayers.value) return;
    psdLayers.value.includeDimensions = psdLayers.value.type == 'CANVA' ? importPageDimensions.value : false;
    showLoader.value = true;
    loaderMessage.value = `Loading ${psdLayers.value.type == 'CANVA' ? 'Canva' : 'PSD'} layers...`;
    showLoader.value = false;
});

const onImportModeChange = async (value: ModalImportMode) => {
    psdHeader.mode = value;
    store.dispatch('storeLayersModal/changeParsedFileImportMode', psdHeader.mode);
    await onPageChange(selectedOption.value)
}

const updateSearchText = (value: string) => {
    searchText.value = value;
};

const extractAndParseNewPageLayers = async () => {
    if (!psdLayers.value || !user.value) return;
    const { designId, selectedPage } = psdLayers.value;
    if (!designId || !selectedPage) return;
    const body: CanvaDesignExportRequest = {
        username: user.value.Username,
        designId,
        format: CanvaExportFormat.PPTX,
        page: selectedPage,
        pngOptions: null
    }
    const exportResponse = await exportCanvaDesign(body);
    if (!exportResponse.succeedd) {
        return;
    }
    const response = await fetch(exportResponse.content);
    const buffer = await response.arrayBuffer();
    await parseAndExtractLayers(buffer, psdLayers.value);
};

const handleImportPngLoading = (makeItTransparent = false) => {
    showLoader.value = !showLoader.value;
    if(makeItTransparent) {
        importingPngTransparent.value = !importingPngTransparent.value;
        return;
    }
    importingPngNonTransparent.value = !importingPngNonTransparent.value;
}

const importAsPngLayer = async (makeItTransparent = false) => {
    if (!psdLayers.value || !user.value) return;
    const { designId, selectedPage } = psdLayers.value;
    if (!designId || !selectedPage) return;
    const page = psdLayers.value.pages.find(page => page.pageNumber === selectedPage);
    if (!page) return

    handleImportPngLoading(makeItTransparent);
    const body: CanvaDesignExportRequest = {
        username: user.value.Username,
        designId,
        format: CanvaExportFormat.PNG,
        page: selectedPage,
        pngOptions: {
            transparent: makeItTransparent
        }
    }
    const exportResponse = await exportCanvaDesign(body);
    if (!exportResponse.succeedd) {
        handleImportPngLoading(makeItTransparent);
        return;
    };
    const [imgBlob, { width, height}] = await Promise.all([fetchImageAsBlob(exportResponse.content), getImageDimensions(exportResponse.content)])
    page.artWorkHeight = height;
    page.artWorkWidth = width

    const imageElement: LayerElementsMetadata = {
        name: psdLayers.value.artworkName,
        left: 0,
        top: 0,
        width,
        height,
        rotation: 0,
        flipX: false,
        flipY: false,
        zIndex: 1,
        visible: true,
        imgSrc: imgBlob,
        type: TreeType.FROM_FILE_IMAGE_ELEMENT
    };
    const imageTreeItem = {
        id: 1,
        name: imageElement.name,
        icon: '',
        type: imageElement.type as TreeType,
        metadata: imageElement
    }
    store.dispatch('storeLayersModal/addSelectedTreeItem', imageTreeItem);
    psdLayers.value.mode = 'PNG';
    emits('fire-import-layers')
}

const onPageChange = async (newPage?: CSelectItem<Primitive> | Primitive) => {
    selectedOption.value = newPage as CSelectItem<number>;
    if (!psdLayers.value?.designId) return;
    if (page.value?.elements.length) return;
    showLoader.value = true;
    clearSelectedTreeItems();
    if (psdHeader.mode == 'LAYERS') {
        await extractAndParseNewPageLayers();
        psdLayers.value = layers.value!;
    }
    store.dispatch('storeLayersModal/setParsedFileLayers', psdLayers.value);
    showLoader.value = false;
};

const clearSelectedTreeItems = () => {
    store.dispatch('storeLayersModal/clearSelectedTreeItems');
};
</script>

<style lang="scss">
@import 'styles/utilities/functions';
.png-transparency-disclaimer {
    padding: 0.625rem 0.75rem;
    border-radius: 0.5rem;
    border: 1px solid get-color('gray-light');
    background: get-color('gray-lightest');

    .notification-message {
        font-size: 0.875rem;
        color: get-color('black');
    }
}
.layer-tree {
    padding-right: 0 !important;
    padding-left: 0 !important;

    .card.p-2 {
        padding: 0.5rem !important;
    }
}
</style>
